import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import SeoOptimizer from './SeoOptimizer'; 
import AdminSpyRadar from './AdminSpyRadar';
import AudienceManager from './AudienceManager'; 
import AnalyticsDashboard from './AnalyticsDashboard'; 
import { db, auth } from '../firebaseConfig';
import { collection, onSnapshot, collectionGroup, getCountFromServer, query, doc, setDoc, updateDoc, limit, getDoc, getDocs, where, addDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { initializeApp } from "firebase/app";
import BusinessProfileDrawer from './BusinessProfileDrawer';
import SocialAutopilot from './SocialAutopilot';
import ChatDrawer from './ChatDrawer'; 
import SeoBlogEngine from './SeoBlogEngine'; 
import AIUsageGraph from './AIUsageGraph';
import WhatsAppAlertBanner from './WhatsAppAlertBanner'; 
import { getAuth as getSecondaryAuth, createUserWithEmailAndPassword, signOut as signOutSecondary } from "firebase/auth";
import { QRCodeSVG } from 'qrcode.react'; 
import AdminSeoController from './AdminSeoController';
import { 
  Server, Users, Activity, Settings, LogOut, Plus, Search, 
  TrendingUp, Zap, Database, Power, AlertTriangle, ShieldAlert,
  CheckCircle2, IndianRupee, X, MessageSquare, Eye, Key, Bell, Rocket, Sparkles, Send, BrainCircuit,
  QrCode, Store, Tag, Filter, ArrowUpDown, Menu, ChevronLeft,
  Clock, Crown, CheckCircle, XCircle, Edit2, Crosshair, Loader2,
  Italic, Strikethrough, RefreshCw, UserPlus, AlertCircle, Bot, Phone, CalendarDays, User, Layers, PenTool, Image as ImageIcon
} from 'lucide-react';

import PricingController from './PricingController'; 
import AdminClientSetup from './AdminClientSetup';

// 🧠 FRONTEND AI: STRICTLY STUDENT OR BUSINESS LOGIC
const categorizeLead = (message, leadData) => {
  if (leadData?.leadType === "STUDENT") return { category: 'STUDENT', score: 1 };
  if (leadData?.leadType === "BUSINESS" || leadData?.source === "B2B_SAAS") return { category: 'BUSINESS', score: 1 };

  if (
      leadData?.mode === "student" || leadData?.mode === "lead" || leadData?.mode === "user" || 
      leadData?.lastAction === "student_mode_active" || leadData?.education !== undefined || 
      leadData?.interest !== undefined || leadData?.type === "Free Demo Class"
  ) {
      return { category: 'STUDENT', score: 0.99 };
  }
  
  if (
      leadData?.mode === "business" || leadData?.mode === "owner_active" || 
      leadData?.mode === "owner_onboarding" || leadData?.businessConfirmed === true || 
      leadData?.businessCandidate
  ) {
      return { category: 'BUSINESS', score: 0.98 };
  }
  
  const fullDocumentText = JSON.stringify(leadData || {}).toLowerCase();
  const businessKeywords = ['demo', 'buy', 'purchase', 'plan', 'subscribe', 'business', 'saas', 'automation', 'bot', 'agency', 'client', 'crm', 'businesscandidate', 'gmb_link_click'];
  const studentKeywords = ['admission', 'fees', 'batch', 'start', 'course', 'join', 'price', 'syllabus', 'exam', 'student', 'study', 'free demo class'];

  if (businessKeywords.some(kw => fullDocumentText.includes(kw))) return { category: 'BUSINESS', score: 0.90 };
  if (studentKeywords.some(kw => fullDocumentText.includes(kw))) return { category: 'STUDENT', score: 0.85 };

  return { category: 'UNKNOWN', score: 0.5 }; 
};

// 📊 Top Level KPI Card (Sleek & Compact)
const MasterKPI = ({ title, value, subtext, icon, isCurrency }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all flex flex-col justify-between h-full group">
    <div className="flex justify-between items-start mb-3">
      <div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{title}</p>
        <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-baseline gap-1">
          {isCurrency && <span className="text-sm md:text-base text-slate-400 font-bold">₹</span>}
          {value}
        </h3>
      </div>
      <div className="p-2 md:p-2.5 bg-slate-50 text-slate-600 rounded-lg border border-slate-100 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors shrink-0">
        {React.cloneElement(icon, { size: 18 })}
      </div>
    </div>
    <div className="flex items-center mt-1">
        <span className="text-[9px] font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100">{subtext}</span>
    </div>
  </div>
);

// ⚪ SideLink Component
const SideLink = ({ icon, label, active, onClick, count }) => (
  <button onClick={onClick} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
    active ? 'bg-indigo-50 text-indigo-700 shadow-sm border border-indigo-100' : 'text-slate-600 hover:bg-slate-50'
  }`}>
    <div className="flex items-center gap-3">
      {icon} <span>{label}</span>
    </div>
    {count !== undefined && count > 0 && (
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm ${active ? 'bg-rose-500 text-white' : 'bg-rose-500 text-white animate-pulse'}`}>{count}</span>
    )}
  </button>
);

const Dashboard = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); 
  const [activeView, setActiveView] = useState('overview'); 
  const [tenants, setTenants] = useState([]);
  const [tenantTab, setTenantTab] = useState('TRIAL');
  const [searchTerm, setSearchTerm] = useState('');
  const [globalLeads, setGlobalLeads] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(true);

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);

  const [leadsList, setLeadsList] = useState([]);
  const [usersList, setUsersList] = useState([]);

  const [inboxFilter, setInboxFilter] = useState('ALL'); 
  const [dateFilter, setDateFilter] = useState('all'); 
  const [customDate, setCustomDate] = useState(''); 
  const [sortOrder, setSortOrder] = useState('newest'); 
  const [crmTab, setCrmTab] = useState('ALL'); 
  const [stageFilter, setStageFilter] = useState('all');
  const [leadTypeFilter, setLeadTypeFilter] = useState('ALL');

  const [selectedChat, setSelectedChat] = useState(null);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false); 
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [generatedPass, setGeneratedPass] = useState("");
  
  const [clientForm, setClientForm] = useState({ 
      name: '', whatsappId: '', email: '', planPrice: 0, aiKnowledge: '', aiRules: '', 
      metaToken: '', coordinatorPhone: '', fbPageId: '', fbPageToken: ''
  });

  const [isResettingId, setIsResettingId] = useState(null);
  const previousLeadsCount = useRef(0);
  const [isRinging, setIsRinging] = useState(false);
  const [toastMsg, setToastMsg] = useState(null); 


  const [isBlastModalOpen, setIsBlastModalOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendClientData, setExtendClientData] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [blastMessage, setBlastMessage] = useState("");
  const [isBlasting, setIsBlasting] = useState(false);
  const [blastAudience, setBlastAudience] = useState("B2C_STUDENTS"); 
  const [blastClientId, setBlastClientId] = useState("");
  const [selectedQrClient, setSelectedQrClient] = useState("");
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState("");

  const playDingSound = () => {
    const audio = new Audio('https://actions.google.com/sounds/v1/alarms/beep_short.ogg');
    audio.play().catch(e => console.log("Audio block"));
    setIsRinging(true);
    setTimeout(() => setIsRinging(false), 3000); 
  };

  const handleFetchTemplates = async () => {
    if (!blastClientId) { alert("⚠️ Target Client select karein!"); return; }
    setIsFetchingTemplates(true);
    try {
        const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/getClientTemplates", {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ clientId: blastClientId })
        });
        const data = await response.json();
        if (data.success) {
            setMetaTemplates(data.templates);
        } else { alert(`❌ Fetch Error: ${data.error}`); }
    } catch (error) { alert(`❌ API Error: ${error.message}`); }
    setIsFetchingTemplates(false);
  };

  const handleTemplateSelect = (e) => {
      const idx = e.target.value;
      setSelectedTemplateIndex(idx);
      if (idx === "") { setBlastMessage(""); return; }
      const selectedTemplate = metaTemplates[idx];
      const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
      if (bodyComponent) {
          let templateText = bodyComponent.text;
          templateText = templateText.replace(/\{\{1\}\}/g, "[Name]");
          templateText = templateText.replace(/\{\{(\d+)\}\}/g, (m, n) => `[Var ${n}]`);
          setBlastMessage(templateText); 
      }
  };

  const handleSendBlast = async () => {
      if(!blastClientId || !blastMessage.trim()) return;
      setIsBlasting(true);
      try {
          const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/launchReactivationBlast", {
              method: "POST", headers: { "Content-Type": "application/json" }, 
              body: JSON.stringify({ targetAudience: blastAudience, messageText: blastMessage, clientId: blastClientId }) 
          });
          const result = await response.json();
          if (response.ok && result.success) {
              alert(`✅ Sent to ${result.sentCount} leads.`);
              setIsBlastModalOpen(false); setBlastMessage(""); setMetaTemplates([]); 
          } else { alert("❌ Error: " + result.error); }
      } catch (error) { alert("❌ Error: " + error.message); }
      setIsBlasting(false);
  };

  useEffect(() => {
    const unsubClients = onSnapshot(collection(db, "clients"), (snap) => {
      setTenants(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const fetchGlobalStats = async () => {
      try {
        setIsLoadingCount(true);
        const snapshot = await getCountFromServer(query(collectionGroup(db, "leads")));
        setGlobalLeads(snapshot.data().count);
      } catch (error) { } finally { setIsLoadingCount(false); }
    };

    const processDocument = (doc) => {
        const leadData = doc.data();
        let displayName = leadData.name || leadData.studentName || leadData.whatsappName || leadData.phone || doc.id; 
        if (displayName === doc.id || displayName === leadData.phone) displayName = `+${displayName.replace(/\D/g, '')}`; 
        if (leadData.businessCandidate) displayName = leadData.businessCandidate.name || displayName; 

        let actualLastMessage = leadData.lastMessage || leadData.message || leadData.text || leadData.content || leadData.lastUserMsg || leadData.query || "";
        if (typeof actualLastMessage === 'object' && actualLastMessage !== null) actualLastMessage = JSON.stringify(actualLastMessage);

        const intelligence = categorizeLead(actualLastMessage, leadData);
        let rawClientId = leadData.clientId || doc.ref.parent?.parent?.id || "Root DB";
        const cleanClientId = rawClientId.replace('client_', ''); 

        return { 
            id: doc.id, ...leadData, category: intelligence.category, 
            realMessageText: actualLastMessage, studentName: displayName, 
            clientId: cleanClientId, dbClientId: rawClientId 
        };
    };

    const unsubLeads = onSnapshot(query(collectionGroup(db, "leads"), limit(1000)), (snap) => {
      const newLeads = snap.docs.map(processDocument);
      setLeadsList(newLeads);
      if (previousLeadsCount.current !== 0) {
          snap.docChanges().forEach((change) => {
              if (change.type === "added") {
                  const newLead = processDocument(change.doc);
                  setToastMsg({ title: "🔥 New Lead Captured!", desc: `${newLead.studentName} just dropped in.`, time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) });
                  playDingSound();
                  setTimeout(() => setToastMsg(null), 6000);
              }
          });
      }
      previousLeadsCount.current = newLeads.length;
    });

    const unsubUsers = onSnapshot(query(collection(db, "users"), limit(1000)), (snap) => {
      setUsersList(snap.docs.map(processDocument));
    });

    fetchGlobalStats();
    return () => { unsubClients(); unsubLeads(); unsubUsers(); };
  }, []);

  const allLeads = [...leadsList, ...usersList].reduce((acc, current) => {
    const x = acc.find(item => item.id === current.id);
    if (!x) return acc.concat([current]);
    else return acc;
  }, []);

  const getValidDateObj = (field) => {
      if (!field) return null;
      if (field.toDate) return field.toDate();
      if (field.toMillis) return new Date(field.toMillis());
      if (typeof field === 'string' || typeof field === 'number') return new Date(field);
      return null;
  };

  const getEntryTime = (lead) => {
      const dateObj = getValidDateObj(lead.createdAt) || getValidDateObj(lead.timestamp) || getValidDateObj(lead.updatedAt);
      return dateObj ? dateObj.getTime() : 0;
  };

  const getActiveTime = (lead) => {
      const dateObj = getValidDateObj(lead.updatedAt) || getValidDateObj(lead.lastFollowUpSentAt) || getValidDateObj(lead.timestamp) || getValidDateObj(lead.createdAt);
      return dateObj ? dateObj.getTime() : 0;
  };

  const processedAdminLeads = allLeads.filter(lead => {
      if (inboxFilter !== 'ALL' && lead.category !== inboxFilter) return false;
      const tenantName = tenants.find(t => t.whatsappId === lead.clientId)?.name || '';
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch = (lead.studentName || "").toLowerCase().includes(searchStr) || tenantName.toLowerCase().includes(searchStr);
      if (searchTerm && !matchesSearch) return false;
      
      if (stageFilter === 'new') {
          const isNew = ['RAW_LEAD', 'NEW_LEAD', 'NEW', 'AWAITING_NAME'].includes(lead.status) || (!lead.status);
          if (!isNew) return false;
      }
      if (stageFilter === 'bot_followed') {
          const isFollowed = ['MESSAGE_SENT', 'FOLLOWED_UP_1', 'FOLLOW_UP', 'NURTURING'].includes(lead.status) || (lead.lastAction === 'bot_reply');
          if (!isFollowed) return false;
      }

      const leadEntryTime = getEntryTime(lead);
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (dateFilter === 'today') return new Date(leadEntryTime).toDateString() === new Date().toDateString();
      if (dateFilter === 'week') return (now - leadEntryTime) < 7 * oneDay;
      if (dateFilter === 'month') return (now - leadEntryTime) < 30 * oneDay;
      if (dateFilter === 'custom' && customDate) return new Date(leadEntryTime).toDateString() === new Date(customDate).toDateString(); 
      
      return true;
  }).sort((a, b) => {
      const timeA = getActiveTime(a);
      const timeB = getActiveTime(b);
      return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
  });

  // Desktop Inline Chat Listener
  useEffect(() => {
    if (!selectedChat) {
      setChatMessages([]);
      return;
    }

    setIsLoadingChats(true);
    const loadingTimeout = setTimeout(() => setIsLoadingChats(false), 5000);
    const cleanPhoneId = String(selectedChat.id).replace(/\D/g, '');
    const currentClientId = selectedChat.clientId || selectedChat.dbClientId;

    let unsubRoot = null;
    let unsubClient = null;
    let allMsgsMap = new Map();

    const updateMessages = () => {
        const sortedMsgs = Array.from(allMsgsMap.values()).sort((a, b) => {
            const timeA = getValidDateObj(a.time) || getValidDateObj(a.timestamp) || getValidDateObj(a.createdAt) || new Date(0);
            const timeB = getValidDateObj(b.time) || getValidDateObj(b.timestamp) || getValidDateObj(b.createdAt) || new Date(0);
            return timeA.getTime() - timeB.getTime();
        });
        setChatMessages(sortedMsgs);
        setIsLoadingChats(false);
        clearTimeout(loadingTimeout);
        setTimeout(() => { 
           if (messagesEndRef.current && messagesEndRef.current.parentNode) {
               messagesEndRef.current.parentNode.scrollTop = messagesEndRef.current.parentNode.scrollHeight;
           }
        }, 100);
    };

    const rootMsgsRef = collection(db, "users", cleanPhoneId, "messages");
    unsubRoot = onSnapshot(rootMsgsRef, (snapshot) => {
        snapshot.forEach(doc => allMsgsMap.set(doc.id, { id: doc.id, ...doc.data() }));
        updateMessages();
    });

    if (currentClientId && currentClientId !== "Root DB") {
        const clientMsgsRef = collection(db, "clients", currentClientId, "chats", cleanPhoneId, "messages");
        unsubClient = onSnapshot(clientMsgsRef, (snapshot) => {
            snapshot.forEach(doc => allMsgsMap.set(doc.id, { id: doc.id, ...doc.data() }));
            updateMessages();
        });
    }

    return () => {
        clearTimeout(loadingTimeout);
        if (unsubRoot) unsubRoot();
        if (unsubClient) unsubClient();
    };
  }, [selectedChat]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    const messagePayload = { role: 'agent', content: newMessage, time: new Date() };

    try {
      setNewMessage('');
      const targetCollection = selectedChat.mode ? "users" : "leads"; 
      await updateDoc(doc(db, targetCollection, selectedChat.id), { botPaused: true });
      const cleanPhoneId = String(selectedChat.id).replace(/\D/g, '');
      const currentClientId = selectedChat.clientId || selectedChat.dbClientId;

      if (currentClientId && currentClientId !== "Root DB") {
          const msgsRef = collection(db, "clients", currentClientId, "chats", cleanPhoneId, "messages");
          await addDoc(msgsRef, messagePayload);
          await updateDoc(doc(db, "clients", currentClientId, "chats", selectedChat.id), { botPaused: true });
      } else {
          const msgsRef = collection(db, "users", cleanPhoneId, "messages");
          await addDoc(msgsRef, messagePayload);
      }
    } catch (error) { console.error("Failed to send message:", error); }
  };

  const handleToggleAi = async () => {
    if (!selectedChat) return;
    const newStatus = !(selectedChat.botMuted || selectedChat.botPaused);
    try {
      const targetCollection = selectedChat.mode ? "users" : "leads"; 
      await updateDoc(doc(db, targetCollection, selectedChat.id), { botPaused: newStatus, botMuted: newStatus });
      const currentClientId = selectedChat.dbClientId;
      if(currentClientId && currentClientId !== "Root DB") {
         await updateDoc(doc(db, "clients", currentClientId, "chats", selectedChat.id), { botPaused: newStatus, botMuted: newStatus });
      }
    } catch (error) {}
  };

  const formatTime = (timeData) => {
    const date = getValidDateObj(timeData);
    if (!date) return '';
    const today = new Date();
    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today, ${timeStr}`;
    return `${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${timeStr}`;
  };

  const togglePremiumStatus = async (clientId, currentPremium) => {
    const actionText = currentPremium ? "DOWNGRADE to Free Trial" : "UPGRADE to Premium VIP";
    if(window.confirm(`Kya aap is client ko ${actionText} karna chahte hain?`)) {
      try { await updateDoc(doc(db, "clients", clientId), { isPremium: !currentPremium, status: !currentPremium ? "ACTIVE" : "TRIAL" }); } catch (error) {}
    }
  };

  // 🚀 EXTEND PREMIUM PLAN (MODAL OPENER)
  const handleExtendPremium = (client) => {
    setExtendClientData(client);
    setExtendDays(30); 
    setIsExtendModalOpen(true);
  };

// 🚀 ACTUAL SAVE LOGIC (INCREASE & DECREASE SUPPORT)
  const confirmExtendPremium = async () => {
    if (!extendClientData) return;
    const client = extendClientData;
    const daysToAdd = Number(extendDays);

    if (isNaN(daysToAdd)) {
      alert("⚠️ Kripya sahi number of days daalein.");
      return;
    }

    try {
      let currentExpiry = getValidDateObj(client.premiumExpiry);
      let newExpiryDate = currentExpiry ? new Date(currentExpiry) : new Date();
      
      // Agar date already expire ho chuki hai, aur hum PLUS (+) kar rahe hain, toh aaj se ginenge.
      if (newExpiryDate < new Date() && daysToAdd > 0) {
        newExpiryDate = new Date();
      }

      newExpiryDate.setDate(newExpiryDate.getDate() + daysToAdd);
      
      // Check if the new date is in the past (Expired)
      const isExpiredNow = newExpiryDate < new Date();
      const newStatus = isExpiredNow ? "SUSPENDED" : "ACTIVE";

      // 1. Dashboard UI Update (Clients Table)
      await updateDoc(doc(db, "clients", client.id), { 
          premiumExpiry: newExpiryDate,
          isPremium: !isExpiredNow,       
          status: newStatus
      });
      
      // 2. 🔥 NUCLEAR FIX: Scan ALL users to find the exact account!
      try {
          const usersSnap = await getDocs(collection(db, "users"));
          const updatePromises = [];
          
          usersSnap.forEach((userDoc) => {
              const uData = userDoc.data();
              const matchEmail = client.email && uData.dashboardEmail === client.email;
              const matchPhone = client.ownerPhone && userDoc.id === String(client.ownerPhone).replace(/\D/g, '');
              const directMatch = userDoc.id === String(client.id).replace(/\D/g, '');
              const matchLinkedId = uData.linkedClientId && (uData.linkedClientId === client.id || uData.linkedClientId === client.whatsappId);
              
              if (matchEmail || matchPhone || directMatch || matchLinkedId) {
                  updatePromises.push(
                      updateDoc(doc(db, "users", userDoc.id), {
                          premiumExpiry: newExpiryDate,
                          isPremium: !isExpiredNow,       
                          status: newStatus,
                          isOfferActive: !isExpiredNow
                      })
                  );
              }
          });
          
          await Promise.all(updatePromises);
          
      } catch (err) {
          console.error("Nuclear sync failed:", err);
      }

      const actionText = daysToAdd >= 0 ? "EXTENDED" : "REDUCED";
      setToastMsg({ 
          title: `PLAN ${actionText} SUCCESSFULLY 🚀`, 
          desc: `${client.name} is now valid till ${newExpiryDate.toLocaleDateString('en-IN')}`, 
          time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
      });
      
      setIsExtendModalOpen(false);
    } catch (error) {
      console.error("Extend error:", error);
      alert("❌ Error adjusting premium plan!");
    }
  };

  const updateLeadStatus = async (lead, newStatus) => {
    try {
        const isRootDb = !lead.dbClientId || lead.dbClientId === "Root DB";
        
        if (isRootDb) {
            const targetCollection = lead.mode ? "users" : "leads";
            await updateDoc(doc(db, targetCollection, lead.id), { status: newStatus, updatedAt: new Date() });
        } else {
            let actualClientId = lead.dbClientId;
            const clientDocRef = doc(db, "clients", actualClientId);
            const clientDocSnap = await getDoc(clientDocRef);
            if (!clientDocSnap.exists()) {
                const clientQuery = await getDocs(query(collection(db, "clients"), where("whatsappId", "==", lead.clientId), limit(1)));
                if (!clientQuery.empty) actualClientId = clientQuery.docs[0].id;
            }
            await updateDoc(doc(db, "clients", actualClientId, "leads", lead.id), { status: newStatus, updatedAt: new Date() });
        }
    } catch (error) { alert("❌ Error updating lead status: " + error.message); }
  };

 const handleSelectChat = async (lead) => {
    setSelectedChat(lead);
    const isNew = !lead.status || ['NEW', 'NEW_LEAD', 'RAW_LEAD', 'AWAITING_NAME'].includes(lead.status);
    if (isNew) {
        await updateLeadStatus(lead, 'VIEWED');
    }
  };

  const handleTransferToCounselor = async (chat) => {
    if (!chat) return;
    
    const currentClientId = chat.clientId || chat.dbClientId;
    
    let coordinatorNumber = "";
    if (typeof tenants !== 'undefined') {
        const tenant = tenants.find(t => t.id === currentClientId || t.whatsappId === currentClientId);
        coordinatorNumber = tenant?.coordinator;
    }

    if (!coordinatorNumber) {
        alert("⚠️ Counselor (Coordinator) ka number set nahi hai! Pehle settings mein number dalein.");
        return;
    }

    if (!window.confirm(`Kya aap is lead ko Counselor (${coordinatorNumber}) ko transfer karna chahte hain?`)) return;

    try {
        try {
             await updateLeadStatus(chat, 'HOT');
        } catch (statusErr) {
             console.log("Status update issue, but continuing...", statusErr);
        }

        if (currentClientId && currentClientId !== "Root DB") {
            const chatRef = doc(db, "clients", currentClientId, "chats", chat.id);
            await setDoc(chatRef, { botPaused: true }, { merge: true }); 
        }

        const chatName = chat.studentName || chat.whatsappName || chat.name || 'A Student';
        const chatPhone = String(chat.id).replace(/\D/g, '');
        const alertMsg = `🚨 *Lead Transferred to You!*\n\n*Name:* ${chatName}\n*Phone:* +${chatPhone}\n*Status:* Hot Lead 🔥\n\nPlease check your dashboard and contact them immediately.`;
        
        window.open(`https://wa.me/${coordinatorNumber}?text=${encodeURIComponent(alertMsg)}`, '_blank');
        
    } catch (error) {
        console.error("Transfer Error Detailed:", error);
        alert(`❌ Error: ${error.message}`); 
    }
  };

  const handleEditName = async (clientId, leadId, currentName, e) => {
    e.stopPropagation(); 
    const newName = window.prompt("Lead ka naya naam enter karein:", currentName !== "New Prospect" ? currentName : "");
    if (newName && newName.trim() !== "") {
        try {
            let actualClientId = clientId;
            const clientDocRef = doc(db, "clients", actualClientId);
            const clientDocSnap = await getDoc(clientDocRef);
            if (!clientDocSnap.exists()) {
                const clientQuery = await getDocs(query(collection(db, "clients"), where("whatsappId", "==", clientId), limit(1)));
                if (!clientQuery.empty) actualClientId = clientQuery.docs[0].id;
            }
            await updateDoc(doc(db, "clients", actualClientId, "leads", leadId), { studentName: newName.trim(), whatsappName: newName.trim(), name: newName.trim(), updatedAt: new Date() });
        } catch (error) {}
    }
  };

  const handleDeployUnit = async (e) => {
    e.preventDefault(); 
    setIsCreating(true);
    const pass = Math.random().toString(36).slice(-8);
    try {
      const secondaryApp = initializeApp(auth.app.options, "SecApp_" + Date.now());
      const secondaryAuth = getSecondaryAuth(secondaryApp);
      const userCred = await createUserWithEmailAndPassword(secondaryAuth, clientForm.email, pass);
      await signOutSecondary(secondaryAuth);
      await setDoc(doc(db, "dashboard_users", userCred.user.uid), { role: "client", clientId: clientForm.whatsappId });
      
     await setDoc(doc(db, "clients", clientForm.whatsappId), { 
          name: clientForm.name, whatsappId: clientForm.whatsappId, email: clientForm.email, 
          metaToken: clientForm.metaToken, coordinator: clientForm.coordinatorPhone,
          fbPageId: clientForm.fbPageId,
          fbPageToken: clientForm.fbPageToken,
          isPremium: Number(clientForm.planPrice) > 0 ? true : false, status: Number(clientForm.planPrice) > 0 ? "ACTIVE" : "TRIAL", 
          planPrice: Number(clientForm.planPrice), createdAt: new Date(), tokensUsed: 0, tokenQuota: 10000, walletBalance: 0,
          aiKnowledge: clientForm.aiKnowledge || `We are ${clientForm.name}. Please contact us for details.`, aiRules: clientForm.aiRules || "Be polite and helpful."
      });
      setGeneratedPass(pass);
    } catch (error) { alert("Error deploying instance: " + error.message); }
    setIsCreating(false);
  };

 const handleGeneratePassword = async (client) => {
    let targetEmail = client.email || client.dashboardEmail;
    if (!targetEmail) {
        targetEmail = window.prompt("⚠️ Is business ka Email system mein save nahi hai.\n\nKripya inka Email ID yahan type karein:");
        if (!targetEmail) return; 
    }
    if (!window.confirm(`Kya aap sach mein ${client.name} ka password reset karna chahte hain?`)) return;
    setIsResettingId(client.id);
    
    try {
        const userToken = await auth.currentUser.getIdToken(); // 👈 Token fetch kiya

        const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/generateClientPassword", { 
            method: "POST", 
            headers: { 
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userToken}` // 👈 Token Header mein bheja
            }, 
            body: JSON.stringify({ 
                email: targetEmail, 
                phoneId: client.coordinator || client.id || client.whatsappId 
            }) 
        });
        
        const data = await response.json();
        if (response.ok && data.success) {
            alert(`✅ Naya Password Ban Gaya!\nEmail: ${data.email}\nPassword: ${data.newPassword}\n(Reset Link generated successfully)`);
        } else alert(`❌ Error: ${data.error}`);
    } catch (error) {
        alert("System Error: Failed to generate password.");
    } finally { 
        setIsResettingId(null); 
    }
  };
  const toggleClientStatus = async (clientId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    if(window.confirm(`Change status to ${newStatus}?`)) {
      try { await updateDoc(doc(db, "clients", clientId), { status: newStatus }); } catch (error) {}
    }
  };

  // 🤖 FEATURE FLAG: LEAD CHATBOT TOGGLE (UPSELL FEATURE)
  const toggleLeadBotStatus = async (clientId, currentStatus) => {
    const newStatus = !currentStatus; // Agar true hai toh false karega, false hai toh true
    const actionText = newStatus ? "ENABLE Auto-Lead Closing Chatbot" : "DISABLE Auto-Lead Chatbot";
    
    if(window.confirm(`Kya aap is client ke liye ${actionText} karna chahte hain?\n(Tip: Charge them extra for this premium feature! 💸)`)) {
      try { 
          await updateDoc(doc(db, "clients", clientId), { leadBotEnabled: newStatus }); 
          // Toast ya alert dikha do
          alert(`✅ Lead Chatbot is now ${newStatus ? 'ON 🟢' : 'OFF 🔴'}!`);
      } catch (error) {
          alert("❌ Error: Toggle fail ho gaya.");
      }
    }
  };

  const needsActionCount = allLeads.filter(l => 
      !l.status || l.status === 'NEW' || l.status === 'NEW_LEAD' || l.status === 'RAW_LEAD' || l.status === 'AWAITING_NAME'
  ).length;

  const filteredTenants = tenants.filter(t => t.name?.toLowerCase().includes(searchTerm.toLowerCase()) || t.whatsappId?.includes(searchTerm));
  const totalMRR = tenants.reduce((acc, curr) => acc + (Number(curr.planPrice) || 0), 0);
  const activeBots = tenants.filter(t => t.status === 'ACTIVE').length;
  const estimatedApiCost = (globalLeads * 1.5).toFixed(0);

  return (
    <div className="flex h-screen bg-[#F8FAFC] text-slate-800 font-sans selection:bg-indigo-100 overflow-hidden relative">
      
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 md:hidden backdrop-blur-sm transition-opacity" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* SIDEBAR */}
      <aside className={`fixed md:relative inset-y-0 left-0 w-[260px] border-r border-slate-200 bg-white flex flex-col shadow-2xl md:shadow-sm z-50 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out`}>
        <div className="h-20 flex items-center px-6 border-b border-slate-100 justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-xl shadow-lg shadow-indigo-200"><Server size={20} className="text-white"/></div>
            <div>
              <span className="font-black text-lg text-slate-900 tracking-tighter uppercase leading-none block">SaaS Engine</span>
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">God Mode</span>
            </div>
          </div>
          <button className="md:hidden text-slate-400 hover:text-slate-600" onClick={() => setIsMobileMenuOpen(false)}><X size={20}/></button>
        </div>
   <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto custom-scrollbar">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-2">Command Center</p>
          <SideLink icon={<Activity size={18}/>} label="Global Overview" active={activeView === 'overview'} onClick={() => {setActiveView('overview'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<TrendingUp size={18}/>} label="Client ROI Reports" active={activeView === 'roi_report'} onClick={() => {setActiveView('roi_report'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Database size={18}/>} label="Audience CRM" active={activeView === 'audience'} onClick={() => {setActiveView('audience'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Crosshair size={18}/>} label="Market Spy Radar" active={activeView === 'spyRadar'} onClick={() => {setActiveView('spyRadar'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Tag size={18}/>} label="SaaS Pricing" active={activeView === 'pricing'} onClick={() => {setActiveView('pricing'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<IndianRupee size={18}/>} label="Financials & Billing" active={activeView === 'billing'} onClick={() => {setActiveView('billing'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Users size={18}/>} label="CRM Pipeline" count={needsActionCount} active={activeView === 'intelligence'} onClick={() => {setActiveView('intelligence'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<ImageIcon size={18}/>} label="Social Autopilot" active={activeView === 'social'} onClick={() => {setActiveView('social'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<QrCode size={18}/>} label="QR & Marketing" active={activeView === 'marketing'} onClick={() => {setActiveView('marketing'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Sparkles size={18}/>} label="SEO Optimizer" active={activeView === 'optimizer'} onClick={() => {setActiveView('optimizer'); setIsMobileMenuOpen(false);}} />
          
          <SideLink icon={<Phone size={18}/>} label="WA API Setup" active={activeView === 'waSetup'} onClick={() => {setActiveView('waSetup'); setIsMobileMenuOpen(false);}} />
            <SideLink icon={<PenTool size={18}/>} label="AI Blog Engine" active={activeView === 'blog'} onClick={() => {setActiveView('blog'); setIsMobileMenuOpen(false);}} />
        </nav>

        <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
          <button onClick={() => signOut(auth)} className="w-full flex items-center justify-center md:justify-start gap-3 px-4 py-3 text-slate-500 hover:bg-white hover:text-rose-600 rounded-xl font-bold text-sm transition-all border border-transparent hover:border-slate-200 hover:shadow-sm"><LogOut size={18}/> Secure Logout</button>
        </div>
      </aside>

     {/* MAIN CONTENT WRAPPER */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-10 w-full">
        
        {/* 🚨 CRITICAL API ALERT BANNER 🚨 */}
        <WhatsAppAlertBanner />

        {/* HEADER */}
        <header className="h-20 px-4 md:px-8 flex items-center justify-between bg-white border-b border-slate-200 gap-2 shrink-0">
          <div className="flex items-center gap-3 w-full md:w-1/3">
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><Menu size={24} /></button>
            <div className="relative w-full">
              <Search size={18} className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="Search leads/tenants..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-inner" />
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-5 flex-shrink-0">
            <div className="relative hidden sm:block" title="New Lead Notifications">
              <Bell size={20} className={`text-slate-600 ${isRinging ? 'animate-bounce text-indigo-600' : ''}`} />
              {isRinging && <span className="absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500"></span></span>}
            </div>
            <button onClick={() => setIsBlastModalOpen(true)} className="bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-bold text-[11px] md:text-sm flex items-center gap-1.5 md:gap-2 border border-rose-200 whitespace-nowrap">
                <Rocket size={16} className="md:w-[18px] md:h-[18px]" /> <span className="hidden sm:inline">Launch Blast</span>
            </button>
            <button onClick={() => { setGeneratedPass(""); setIsModalOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 md:px-5 py-2 md:py-2.5 rounded-xl font-bold text-[11px] md:text-sm flex items-center gap-1.5 md:gap-2 shadow-lg shadow-indigo-200 whitespace-nowrap">
                <Plus size={16} className="md:w-[18px] md:h-[18px]"/> <span className="hidden sm:inline">Deploy Unit</span>
            </button>
          </div>
        </header>

        <main className={`flex-1 flex flex-col bg-[#F0F2F5] min-h-0 ${activeView === 'intelligence' ? 'overflow-hidden p-0 h-full' : 'overflow-y-auto custom-scrollbar p-4 md:p-8'}`}>

          {/* ========================================= */}
          {/* 🚀 ADMIN CLIENT ROI REPORTS VIEW */}
          {/* ========================================= */}
          {activeView === 'roi_report' && (
              <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight uppercase">Growth Reports</h3>
                        <p className="text-xs md:text-sm font-medium text-slate-500 mt-1">Select a client to view their AI performance.</p>
                    </div>
                    <select 
                        value={selectedQrClient} 
                        onChange={(e) => setSelectedQrClient(e.target.value)} 
                        className="w-full md:w-64 p-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 bg-slate-50 cursor-pointer shadow-inner"
                    >
                        <option value="">-- Select Business --</option>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                
                {selectedQrClient ? (
                    <AnalyticsDashboard clientId={selectedQrClient} />
                ) : (
                    <div className="text-center p-12 text-slate-400 bg-white rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                        <TrendingUp size={48} className="mb-4 opacity-50" />
                        <p className="font-bold text-lg text-slate-500">No Client Selected</p>
                        <p className="text-sm">Please select a business from the dropdown above to view their metrics.</p>
                    </div>
                )}
              </div>
          )}
          
      {/* ========================================= */}
          {/* 🚀 OVERVIEW VIEW */}
          {/* ========================================= */}
          {activeView === 'overview' && (
              <div className="max-w-7xl mx-auto space-y-4 md:space-y-5 animate-in fade-in duration-500">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  <MasterKPI title="Monthly Rec. Revenue" value={totalMRR.toLocaleString()} subtext="Active subs" icon={<TrendingUp size={20} />} isCurrency={true} />
                  <MasterKPI title="Active Instances" value={activeBots} subtext="Running Bots" icon={<Server size={20} />} />
                  <MasterKPI title="Global Leads Captured" value={isLoadingCount ? "..." : globalLeads.toLocaleString()} subtext="Total processed" icon={<Users size={20} />} />
                  <MasterKPI title="Est. API Cost (Burn)" value={estimatedApiCost} subtext="OpenAI+Meta" icon={<Zap size={20} />} isCurrency={true} />
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-6 md:mt-8">
                    <AIUsageGraph />
                </div>

                {/* SAAS TENANT MATRIX */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col mt-6 md:mt-8">
                  <div className="px-4 md:px-6 py-4 md:py-5 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <h3 className="text-xs md:text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <Users size={16} className="text-indigo-500" /> SaaS Tenant Matrix
                      </h3>
                      <p className="text-[10px] md:text-xs font-medium text-slate-500 mt-1">Manage Trial and Paid instances.</p>
                    </div>

                    <div className="flex bg-slate-200/50 p-1.5 rounded-xl shadow-inner w-full md:w-auto">
                        <button 
                            onClick={() => setTenantTab('TRIAL')}
                            className={`flex-1 md:flex-none items-center justify-center gap-2 px-5 py-2 rounded-lg text-[10px] md:text-xs font-black uppercase tracking-wider transition-all flex ${
                                tenantTab === 'TRIAL'
                                ? 'bg-white text-slate-800 shadow-sm border border-slate-200' 
                                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent'
                            }`}
                        >
                            <Clock size={14} className={tenantTab === 'TRIAL' ? 'text-amber-500' : 'text-slate-400'}/> 
                            Free Trials ({tenants.filter(t => !t.isPremium || t.isPremium === "false").length})
                        </button>
                        <button 
                            onClick={() => setTenantTab('PREMIUM')}
                            className={`flex-1 md:flex-none items-center justify-center gap-2 px-5 py-2 rounded-lg text-[10px] md:text-xs font-black uppercase tracking-wider transition-all flex ${
                                tenantTab === 'PREMIUM' 
                                ? 'bg-indigo-600 text-white shadow-md border border-indigo-700' 
                                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent'
                            }`}
                        >
                            <Crown size={14} className={tenantTab === 'PREMIUM' ? 'text-amber-300' : 'text-slate-400'}/> 
                            Premium Paid ({tenants.filter(t => t.isPremium && t.isPremium !== "false").length})
                        </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto w-full">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                      <thead>
                        <tr className="bg-white border-b border-slate-200 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400">
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[20%]">Business</th>
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[15%]">Engine ID</th>
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[15%]">Meta Setup</th>
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[15%]">AI Quota</th>
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[20%]">Bot Activity</th>
                          <th className="px-4 md:px-6 py-3 md:py-4 w-[15%] text-right">Controls</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {filteredTenants
                          .filter(t => tenantTab === 'PREMIUM' ? (t.isPremium && t.isPremium !== "false") : (!t.isPremium || t.isPremium === "false"))
                          .map((client) => {
                          const usagePercent = Math.min(((client.tokensUsed || 0) / (client.tokenQuota || 10000)) * 100, 100).toFixed(0);
                          const isHighUsage = usagePercent > 85;
                          
                          return (
                            <tr key={client.id} className={`hover:bg-slate-50 transition-colors group ${client.status === 'SUSPENDED' ? 'opacity-60 grayscale' : ''}`}>
                              <td className="px-4 md:px-6 py-3 md:py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${client.isPremium ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                                    {client.isPremium ? <Crown size={14}/> : <Store size={14}/>}
                                  </div>
                                  <div>
                                    <p className="text-xs md:text-sm font-bold text-slate-900 leading-tight">{client.name}</p>
                                    <p className="text-[9px] md:text-[10px] text-slate-400 font-medium mt-0.5">{client.email || 'No Email'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 md:px-6 py-3 md:py-4">
                                <p className="text-[9px] md:text-[10px] font-mono font-medium text-slate-600 bg-slate-100 border border-slate-200 inline-block px-2 py-1 rounded shadow-inner truncate max-w-[120px]">{client.whatsappId}</p>
                              </td>
                              <td className="px-4 md:px-6 py-3 md:py-4">
                                <div className="flex flex-col gap-1.5 items-start">
                                  <span className={`px-2 py-0.5 text-[8px] md:text-[9px] font-black rounded-md uppercase tracking-widest border ${client.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                                    {client.status || 'ACTIVE'}
                                  </span>
                                  <span className={`flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider ${client.metaToken ? 'text-emerald-500' : 'text-slate-400'}`}>
                                    {client.metaToken ? <CheckCircle2 size={10}/> : <AlertTriangle size={10}/>} 
                                    {client.metaToken ? 'API Linked' : 'No Token'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 md:px-6 py-3 md:py-4">
                                <div className="w-16 md:w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1 shadow-inner">
                                  <div style={{width: `${usagePercent}%`}} className={`h-full transition-all ${isHighUsage ? 'bg-rose-500' : 'bg-indigo-500'}`}></div>
                                </div>
                                <p className={`text-[9px] md:text-[10px] font-bold ${isHighUsage ? 'text-rose-500' : 'text-slate-400'}`}>{usagePercent}% Consumed</p>
                              </td>
                              <td className="px-4 md:px-6 py-3 md:py-4">
                                <div className="flex flex-col gap-1 w-full max-w-[130px]">
                                    <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex justify-between shadow-sm border border-indigo-100">
                                        <span>Meta Posts:</span> 
                                        <span>{client.totalMetaPosts || 0}</span>
                                    </span>
                                    <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded flex justify-between shadow-sm border border-emerald-100">
                                        <span>GMB Replies:</span> 
                                        <span>{client.totalReviewsReplied || 0}</span>
                                    </span>
                                    <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded flex justify-between shadow-sm border border-rose-100">
                                        <span>AI Chats:</span> 
                                        <span>{client.totalAiMessages || 0}</span>
                                    </span>
                                </div>
                              </td>
                              <td className="px-4 md:px-6 py-3 md:py-4 text-right flex items-center justify-end gap-1.5 md:gap-2">
                                
                                {/* 📅 EXTEND EXPIRY BUTTON */}
                                <button 
                                  onClick={() => handleExtendPremium(client)} 
                                  className="p-1.5 border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 text-slate-500 rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8"
                                  title="Extend Premium Expiry"
                                >
                                  <CalendarDays size={14} />
                                </button>
                                
                                {/* 👑 VIP TOGGLE BUTTON */}
                                <button 
                                  onClick={() => togglePremiumStatus(client.id, client.isPremium)} 
                                  className={`p-1.5 border border-slate-200 bg-white rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8 ${client.isPremium ? 'text-amber-500 hover:bg-amber-50 hover:border-amber-200' : 'text-slate-500 hover:text-amber-500 hover:bg-amber-50'}`} 
                                  title={client.isPremium ? "Remove Premium" : "Make Premium VIP"}
                                >
                                  <Crown size={14} />
                                </button>
                                
                                {/* 👁️ VIEW PROFILE BUTTON */}
                                <button 
                                  onClick={() => { setSelectedBusiness(client); setIsProfileDrawerOpen(true); }} 
                                  className="p-1.5 border border-slate-200 bg-white hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 text-slate-500 rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8" 
                                  title="View Profile"
                                >
                                  <Eye size={14} />
                                </button>
                                
                                {/* 🔑 RESET PASSWORD BUTTON */}
                                <button 
                                  onClick={() => handleGeneratePassword(client)} 
                                  disabled={isResettingId === client.id} 
                                  className="p-1.5 border border-slate-200 bg-white hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 text-slate-500 rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8 disabled:opacity-50" 
                                  title="Reset Password & Send"
                                >
                                  {isResettingId === client.id ? <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div> : <Key size={14} />}
                                </button>
                                
                                {/* 🔌 POWER TOGGLE BUTTON */}
                                <button 
                                  onClick={() => toggleClientStatus(client.id, client.status)} 
                                  className="p-1.5 border border-slate-200 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 text-slate-500 rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8" 
                                  title="Toggle Bot Status"
                                >

                                  {/* 🤖 LEAD BOT TOGGLE BUTTON (THE UPSELL) */}
      <button 
        onClick={() => toggleLeadBotStatus(client.id, client.leadBotEnabled)} 
        className={`p-1.5 border border-slate-200 bg-white rounded-lg transition-all shadow-sm flex items-center justify-center h-8 w-8 ${client.leadBotEnabled ? 'text-violet-600 bg-violet-50 border-violet-200' : 'text-slate-400 hover:text-violet-500 hover:bg-violet-50'}`} 
        title={client.leadBotEnabled ? "Disable Lead Chatbot" : "Enable Lead Chatbot (Premium)"}
      >
        <Bot size={14} />
      </button>
                                  <Power size={14} />
                                </button>

                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
          )}

          {/* ========================================= */}
          {/* 🚀 PREMIUM CRM PIPELINE */}
          {/* ========================================= */}
          {activeView === 'intelligence' && (() => {
              const typeFilteredLeads = processedAdminLeads;

              const categorizedLeads = {
                  ALL: typeFilteredLeads,
                  ACTION_REQUIRED: typeFilteredLeads.filter(l =>
                      (l.status === 'HOT_LEAD' || l.status === 'REACTIVATED_HOT' || l.status === 'HOT' || l.botMuted === true) && !String(l.status).includes('CLOSE') && !String(l.status).includes('DEAD') && !String(l.status).includes('DROP') && !String(l.status).includes('WON')
                  ),
                  BOT_WORKING: typeFilteredLeads.filter(l =>
                      (l.status === 'RAW_LEAD' || l.status === 'NEW_LEAD' || l.status === 'NEW' || l.status === 'VIEWED' || l.status === 'AWAITING_NAME' || l.status === 'NURTURING' || !l.status) && !l.botMuted && !String(l.status).includes('CLOSE') && !String(l.status).includes('DEAD') && !String(l.status).includes('DROP') && !String(l.status).includes('WON')
                  ),
                  IN_PROGRESS: typeFilteredLeads.filter(l =>
                      l.status === 'MESSAGE_SENT' || l.status === 'FOLLOWED_UP_1' || l.status === 'FOLLOW_UP'
                  ),
                  ARCHIVED: typeFilteredLeads.filter(l =>
                      String(l.status).includes('CLOSE') || String(l.status).includes('DEAD') || String(l.status).includes('DROP') || String(l.status).includes('WON') || String(l.status).includes('DND')
                  )
              };

              const isSelectedAiPaused = selectedChat?.botMuted || selectedChat?.botPaused;

              // ── TAB CONFIG ──────────────────────────────────────────
              const tabs = [
                { key: 'ALL',             label: 'All',          emoji: '💬', color: 'indigo', count: categorizedLeads.ALL.length },
                { key: 'ACTION_REQUIRED', label: 'Needs Reply',  emoji: '🔥', color: 'rose',   count: categorizedLeads.ACTION_REQUIRED.length },
                { key: 'BOT_WORKING',     label: 'Bot Active',   emoji: '🤖', color: 'violet', count: categorizedLeads.BOT_WORKING.length },
                { key: 'IN_PROGRESS',     label: 'Follow Up',    emoji: '📋', color: 'amber',  count: categorizedLeads.IN_PROGRESS.length },
                { key: 'ARCHIVED',        label: 'Closed',       emoji: '✅', color: 'slate',  count: categorizedLeads.ARCHIVED.length },
              ];

              const tabColorMap = {
                indigo: { active: 'bg-indigo-50 text-indigo-700 border-indigo-500', badge: 'bg-indigo-100 text-indigo-700' },
                rose:   { active: 'bg-rose-50 text-rose-700 border-rose-500',       badge: 'bg-rose-100 text-rose-700' },
                violet: { active: 'bg-violet-50 text-violet-700 border-violet-500', badge: 'bg-violet-100 text-violet-700' },
                amber:  { active: 'bg-amber-50 text-amber-700 border-amber-500',    badge: 'bg-amber-100 text-amber-700' },
                slate:  { active: 'bg-slate-100 text-slate-600 border-slate-400',   badge: 'bg-slate-200 text-slate-600' },
              };

              return (
              <div className="h-full flex w-full bg-[#f5f6fa] relative flex-col md:flex-row min-h-0">

                {/* ══════════════════════════════════════════
                    LEFT PANEL — CONTACT LIST
                ══════════════════════════════════════════ */}
                <div className={`w-full md:w-[320px] lg:w-[360px] border-r border-slate-200 flex flex-col bg-white shrink-0 z-20 ${selectedChat ? 'hidden md:flex' : 'flex'}`}>

                  {/* Header */}
                  <div className="px-4 pt-4 pb-3 border-b border-slate-100 shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-[17px] font-bold text-slate-800">Live Chats</h2>
                      <span className="text-[11px] font-semibold bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full border border-indigo-100">
                        {categorizedLeads.ALL.length} contacts
                      </span>
                    </div>

                    {/* Search */}
                    <div className="relative mb-3">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search by name or number..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 text-slate-700 placeholder:text-slate-400 transition-all"
                      />
                    </div>

                    {/* Type Filter */}
                    <div className="flex bg-slate-100 p-0.5 rounded-lg gap-0.5">
                      {[
                        { key: 'ALL',      label: 'All' },
                        { key: 'STUDENT',  label: '🎓 Students' },
                        { key: 'BUSINESS', label: '🏢 B2B' },
                      ].map(f => (
                        <button
                          key={f.key}
                          onClick={() => setInboxFilter(f.key)}
                          className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-all ${
                            inboxFilter === f.key
                              ? 'bg-white text-indigo-600 shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >{f.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Status Tabs */}
                  <div className="flex border-b border-slate-200 bg-white shrink-0 overflow-x-auto">
                    {tabs.map(tab => {
                      const isActive = crmTab === tab.key;
                      const colors = tabColorMap[tab.color];
                      return (
                        <button
                          key={tab.key}
                          onClick={() => setCrmTab(tab.key)}
                          className={`flex-1 min-w-[60px] flex flex-col items-center py-2.5 px-1 text-[10px] font-bold border-b-2 transition-all gap-0.5 ${
                            isActive
                              ? `${colors.active} border-b-2`
                              : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className="text-base leading-none">{tab.emoji}</span>
                          <span className="uppercase tracking-wide leading-none mt-0.5">{tab.label}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${isActive ? colors.badge : 'bg-slate-100 text-slate-500'}`}>
                            {tab.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Date filter */}
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0 bg-slate-50/60">
                    <select
                      value={dateFilter}
                      onChange={e => setDateFilter(e.target.value)}
                      className="flex-1 bg-white border border-slate-200 rounded-lg text-[11px] font-medium text-slate-600 px-2 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="custom">Custom Date</option>
                    </select>
                    {dateFilter === 'custom' && (
                      <input
                        type="date"
                        value={customDate}
                        onChange={e => setCustomDate(e.target.value)}
                        className="flex-1 bg-white border border-indigo-200 rounded-lg text-[11px] font-medium text-indigo-600 px-2 py-1.5 outline-none cursor-pointer"
                      />
                    )}
                  </div>

                  {/* Contact List */}
                  <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {categorizedLeads[crmTab].length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
                        <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center">
                          <MessageSquare size={24} className="text-slate-300" />
                        </div>
                        <p className="text-[13px] font-semibold text-slate-600">No chats here</p>
                        <p className="text-[11px] text-slate-400">No conversations match this filter.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col divide-y divide-slate-100">
                        {categorizedLeads[crmTab].map(lead => {
                          const isSelected = selectedChat?.id === lead.id;
                          const tenantName = tenants.find(t => t.whatsappId === lead.clientId)?.name || 'Direct';

                          const entryTime = getValidDateObj(lead.createdAt) || getValidDateObj(lead.timestamp) || getValidDateObj(lead.updatedAt) || new Date();
                          const now = new Date();
                          const isToday = now.toDateString() === entryTime.toDateString();
                          const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
                          const isYesterday = yesterday.toDateString() === entryTime.toDateString();
                          let displayDate = isToday ? entryTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : isYesterday ? 'Yesterday' : entryTime.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

                          const realName = (lead.studentName && lead.studentName !== 'New Prospect') ? lead.studentName : (lead.whatsappName || lead.name || lead.phone || 'Unknown');
                          const avatarChar = /[a-zA-Z]/.test(realName) ? realName.match(/[a-zA-Z]/)[0].toUpperCase() : (lead.phone ? lead.phone.toString().slice(-2) : '?');
                          const finalAvatarUrl = lead.profilePic || lead.profile_pic || lead.avatarUrl || null;

                          let displayMsg = lead.realMessageText || lead.lastMessage || lead.message || 'Started conversation...';
                          if (displayMsg?.includes('Auto-Flow')) displayMsg = 'Bot is handling automated sequence...';
                          if (displayMsg === '[Media/Interactive Option Sent]') displayMsg = 'Interactive Menu Sent';

                          const isBotActive = !lead.botMuted && !lead.botPaused;
                          const isHot = lead.status === 'HOT_LEAD' || lead.status === 'HOT';
                          const isUnread = lead.status === 'NEW';

                          // Source badge
                          let sourceBadge = null;
                          if (lead.source === 'B2B_SAAS' || lead.businessCandidate) {
                            sourceBadge = <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded">🏢 GMB</span>;
                          } else if (String(lead.source || '').toLowerCase().includes('fb') || String(lead.source || '').toLowerCase().includes('meta')) {
                            sourceBadge = <span className="text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded">📘 FB Ad</span>;
                          } else {
                            sourceBadge = <span className="text-[9px] font-bold bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded">💬 Organic</span>;
                          }

                          const avatarColors = ['bg-indigo-400', 'bg-emerald-400', 'bg-rose-400', 'bg-amber-400', 'bg-violet-400'];
                          const avatarColor = avatarColors[realName.charCodeAt(0) % 5];

                          return (
                            <div
                              key={lead.id}
                              onClick={() => handleSelectChat(lead)}
                              className={`px-3 py-3 cursor-pointer flex items-start gap-3 transition-all relative hover:bg-slate-50 ${isSelected ? 'bg-indigo-50 border-l-[3px] border-indigo-500' : 'border-l-[3px] border-transparent'}`}
                            >
                              {/* Avatar */}
                              <div className="relative shrink-0">
                                {finalAvatarUrl ? (
                                  <img src={finalAvatarUrl} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                                ) : (
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[14px] ${avatarColor}`}>
                                    {avatarChar}
                                  </div>
                                )}
                                {/* Bot/Human dot */}
                                <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${isBotActive ? 'bg-emerald-400' : 'bg-amber-400'}`} title={isBotActive ? 'Bot Active' : 'Human Mode'} />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-1 mb-0.5">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <span className={`font-semibold text-[13px] truncate ${isSelected ? 'text-indigo-700' : 'text-slate-800'}`}>
                                      {realName}
                                    </span>
                                    {isHot && <span className="text-[10px]">🔥</span>}
                                    {isUnread && <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse shrink-0" />}
                                  </div>
                                  <span className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">{displayDate}</span>
                                </div>

                                <p className="text-[11px] text-slate-500 truncate mb-1.5">
                                  {isBotActive ? <span className="text-violet-500">🤖 </span> : <span className="text-amber-500">🧑 </span>}
                                  {displayMsg}
                                </p>

                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[9px] font-medium text-slate-400 truncate max-w-[70px]">📍 {tenantName}</span>
                                  {sourceBadge}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ══════════════════════════════════════════
                    MIDDLE + RIGHT — CHAT AREA
                ══════════════════════════════════════════ */}
                <div className={`flex-1 flex-col relative min-h-0 overflow-hidden ${!selectedChat ? 'hidden md:flex' : 'flex w-full'}`}>
                  {selectedChat ? (() => {
                    const sidebarEntryTime = getValidDateObj(selectedChat.createdAt) || getValidDateObj(selectedChat.timestamp) || getValidDateObj(selectedChat.updatedAt) || new Date();
                    const headerRealName = (selectedChat.studentName && selectedChat.studentName !== 'New Prospect') ? selectedChat.studentName : (selectedChat.whatsappName || selectedChat.name || selectedChat.phone || 'Unknown');
                    const headerAvatarChar = /[a-zA-Z]/.test(headerRealName) ? headerRealName.match(/[a-zA-Z]/)[0].toUpperCase() : '?';
                    const headerAvatarUrl = selectedChat.profilePic || selectedChat.profile_pic || selectedChat.avatarUrl || null;
                    const avatarColors = ['bg-indigo-400', 'bg-emerald-400', 'bg-rose-400', 'bg-amber-400', 'bg-violet-400'];
                    const headerAvatarColor = avatarColors[headerRealName.charCodeAt(0) % 5];

                    return (
                    <div className="flex w-full min-h-0" style={{height: '100%'}}>

                      {/* ── CHAT COLUMN ── */}
                      <div className="flex-1 flex flex-col min-w-0" style={{minHeight: 0, height: '100%'}}>

                        {/* Chat Header */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 shrink-0 shadow-sm">
                          {/* Back on mobile */}
                          <button onClick={() => handleSelectChat(null)} className="md:hidden text-slate-500 hover:text-slate-700 mr-1">
                            <ChevronLeft size={20} />
                          </button>

                          {headerAvatarUrl ? (
                            <img src={headerAvatarUrl} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0" />
                          ) : (
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[14px] shrink-0 ${headerAvatarColor}`}>
                              {headerAvatarChar}
                            </div>
                          )}

                      <div className="flex-1 min-w-0 pr-2">
                            {/* Naam ko alag line mein poori jagah di hai */}
                            <h3 className="font-bold text-slate-900 text-[15px] leading-tight truncate">
                                {headerRealName}
                            </h3>
                            
                            {/* Number aur Bot status ko ek sath niche shift kiya hai */}
                            <div className="flex items-center gap-2 mt-1 overflow-hidden">
                               <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded shadow-sm shrink-0">
                                  +{selectedChat?.phone || String(selectedChat?.id).replace(/\D/g, '')}
                               </span>
                               
                               <span className="text-slate-300 shrink-0">•</span>

                               <p className="text-[10px] md:text-[11px] font-medium text-slate-500 flex items-center gap-1 truncate">
                                 <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelectedAiPaused ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                                 {isSelectedAiPaused ? 'Human mode' : 'Bot active'}
                               </p>
                            </div>
                          </div>
                         {/* Action buttons */}
                          <div className="flex items-center gap-2 shrink-0">
                            
                            {/* 🚀 NEW TRANSFER BUTTON */}
                            <button
                              onClick={() => handleTransferToCounselor(selectedChat)}
                              className="text-[11px] font-black text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 shadow-sm uppercase tracking-wide"
                              title="Assign to Counselor"
                            >
                              <UserPlus size={14} /> Transfer
                            </button>

                            <button
                              onClick={() => updateLeadStatus(selectedChat, 'HOT')}
                              className="text-[11px] font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1"
                            >
                              <TrendingUp size={12} /> Hot
                            </button>
                            <button
                              onClick={() => updateLeadStatus(selectedChat, 'DEAD')}
                              className="text-[11px] font-semibold text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1"
                            >
                              <XCircle size={12} /> Close
                            </button>

                            {/* Bot toggle */}
                            <button
                              onClick={async () => {
                                const newStatus = !isSelectedAiPaused;
                                try {
                                  const currentClientId = selectedChat.clientId;
                                  if (currentClientId && currentClientId !== 'Root DB') {
                                    await import('firebase/firestore').then(({ doc, updateDoc }) =>
                                      updateDoc(doc(db, 'clients', currentClientId, 'chats', selectedChat.id), { botPaused: newStatus })
                                    );
                                  }
                                  await import('firebase/firestore').then(({ doc, updateDoc }) =>
                                    updateDoc(doc(db, selectedChat.mode ? 'users' : 'leads', selectedChat.id), { botPaused: newStatus })
                                  );
                                } catch (e) { console.error(e); }
                              }}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${
                                isSelectedAiPaused
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              }`}
                            >
                              {isSelectedAiPaused ? <><User size={12} /> Take Over</> : <><Bot size={12} /> Bot ON</>}
                            </button>
                          </div>
                        </div>

                        {/* AI Paused Banner */}
                        {isSelectedAiPaused && (
                          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 flex items-center gap-2 text-[11px] font-semibold shrink-0">
                            <ShieldAlert size={13} />
                            Bot is paused — your messages will be sent directly to the customer
                          </div>
                        )}

                        {/* Messages */}
                        <div
                          className="overflow-y-auto custom-scrollbar p-4 space-y-1"
                          style={{flex: "1 1 0", minHeight: 0,
                            background: '#efeae2',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4c9bc' fill-opacity='0.25'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                          }}
                        >
                          {isLoadingChats ? (
                            <div className="flex flex-col items-center justify-center h-full gap-3">
                              <Loader2 size={24} className="animate-spin text-indigo-400" />
                              <p className="text-[12px] text-slate-500 font-medium">Loading messages...</p>
                            </div>
                          ) : chatMessages.length > 0 ? (
                            <>
                              <div className="flex justify-center mb-3">
                                <span className="bg-white/80 text-slate-500 text-[10px] font-semibold px-3 py-1 rounded-full shadow-sm flex items-center gap-1.5">
                                  <CalendarDays size={11} /> Chat History
                                </span>
                              </div>
                              {chatMessages.map((msg, index) => {
                                const isUser = msg.role === 'user' || msg.sender === 'user';
                                const isAgent = msg.role === 'agent' || msg.sender === 'agent';
                                const rawMsgText = msg.content || msg.text || msg.message || '';
                                const interactiveBody = msg.interactive?.body?.text || msg.body?.text || '';
                                const interactiveHeader = msg.interactive?.header?.text || msg.header?.text || '';
                                const interactiveButtons = msg.interactive?.action?.buttons || msg.interactive?.action?.sections?.[0]?.rows || msg.buttons || [];
                                const isMediaOption = rawMsgText === '[Media/Interactive Option Sent]';

                                // Date separator
                                const msgDate = msg.time?.toDate ? msg.time.toDate() : (msg.time ? new Date(msg.time) : null);
                                const prevMsg = chatMessages[index - 1];
                                const prevDate = prevMsg ? (prevMsg.time?.toDate ? prevMsg.time.toDate() : (prevMsg.time ? new Date(prevMsg.time) : null)) : null;
                                const showDateSep = msgDate && (!prevDate || msgDate.toDateString() !== prevDate.toDateString());
                                const today = new Date();
                                const yesterday2 = new Date(today); yesterday2.setDate(yesterday2.getDate() - 1);
                                let dateLabel = msgDate ? msgDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
                                if (msgDate?.toDateString() === today.toDateString()) dateLabel = 'Today';
                                else if (msgDate?.toDateString() === yesterday2.toDateString()) dateLabel = 'Yesterday';

                                return (
                                  <React.Fragment key={index}>
                                    {showDateSep && (
                                      <div className="flex justify-center my-3">
                                        <span className="bg-white/80 text-slate-500 text-[10px] font-semibold px-3 py-1 rounded-full shadow-sm">{dateLabel}</span>
                                      </div>
                                    )}
                                    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                                      <div className={`max-w-[75%] flex flex-col ${isUser ? 'items-start' : 'items-end'}`}>
                                        <div className={`px-3 py-2 shadow-sm text-[13px] leading-relaxed ${
                                          isUser
                                            ? 'bg-white text-slate-800 rounded-2xl rounded-bl-sm'
                                            : isAgent
                                              ? 'bg-indigo-600 text-white rounded-2xl rounded-br-sm'
                                              : 'bg-[#dcf8c6] text-slate-800 rounded-2xl rounded-br-sm'
                                        }`} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                          {msg.mediaUrl || msg.imageUrl ? (
                                            <div className="flex flex-col gap-1.5">
                                              <img src={msg.mediaUrl || msg.imageUrl} alt="Media" className="max-w-[200px] max-h-[200px] rounded-lg object-cover" />
                                              {!isMediaOption && rawMsgText && <p className="whitespace-pre-wrap">{rawMsgText}</p>}
                                            </div>
                                          ) : (msg.type === 'interactive' || msg.buttons || isMediaOption) ? (
                                            <div className="flex flex-col gap-2">
                                              {/* Header */}
                                              {interactiveHeader ? (
                                                <p className="text-[11px] font-bold text-current/70 uppercase tracking-wide">{interactiveHeader}</p>
                                              ) : null}
                                              {/* Body text */}
                                              <p className="whitespace-pre-wrap text-[13px]">
                                                {interactiveBody || (!isMediaOption ? rawMsgText : 'Please select an option below:')}
                                              </p>
                                              {/* Buttons */}
                                              {interactiveButtons.length > 0 ? (
                                                <div className="flex flex-col gap-1 pt-1.5 border-t border-current/10">
                                                  {interactiveButtons.map((btn, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-current/10 border border-current/15">
                                                      <span className="w-4 h-4 rounded-full bg-current/20 flex items-center justify-center text-[9px]">{i+1}</span>
                                                      {btn.reply?.title || btn.title || btn}
                                                    </div>
                                                  ))}
                                                </div>
                                              ) : isMediaOption ? (
                                                <div className="flex items-center gap-1.5 text-[11px] text-current/50 border-t border-current/10 pt-1.5">
                                                  <Layers size={12} /> WhatsApp pe options dikhte hain
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : (
                                            <p className="whitespace-pre-wrap break-words">{rawMsgText}</p>
                                          )}
                                          <div className={`text-[10px] mt-1 flex items-center justify-end gap-1 ${isUser ? 'text-slate-400' : isAgent ? 'text-indigo-200' : 'text-slate-500'}`}>
                                            {formatTime(msg.time)}
                                            {!isUser && <CheckCircle size={12} className={isAgent ? 'text-indigo-200' : 'text-emerald-500'} />}
                                          </div>
                                        </div>
                                        <span className="text-[9px] text-slate-400 mt-0.5 px-1 font-medium">
                                          {isUser ? 'Customer' : isAgent ? '🧑 Agent' : '🤖 Bot'}
                                        </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                              <div ref={messagesEndRef} className="h-2" />
                            </>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full gap-3">
                              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <MessageSquare size={24} className="text-slate-300" />
                              </div>
                              <p className="text-[13px] font-semibold text-slate-600">No messages yet</p>
                              {(selectedChat.realMessageText || selectedChat.lastMessage) && (
                                <div className="mt-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[11px] text-slate-500 max-w-xs text-center shadow-sm italic">
                                  "{selectedChat.realMessageText || selectedChat.lastMessage}"
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Input */}
                        <div className="px-4 py-3 bg-white border-t border-slate-200 shrink-0">
                          <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                            <div className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-100 transition-all">
                              <input
                                type="text"
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                placeholder={isSelectedAiPaused ? "Type your reply..." : "Type to take over from bot..."}
                                className="w-full bg-transparent px-4 py-3 text-[13px] text-slate-700 outline-none placeholder:text-slate-400"
                              />
                            </div>
                            <button
                              type="submit"
                              disabled={!newMessage.trim()}
                              className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-full flex items-center justify-center transition-all shrink-0 shadow-md active:scale-95"
                            >
                              <Send size={16} className="ml-0.5" />
                            </button>
                          </form>
                          <p className="text-[10px] text-slate-400 text-center mt-2 flex items-center justify-center gap-1">
                            <Bot size={10} className="text-indigo-400" />
                            {isSelectedAiPaused ? 'Bot paused — replying as agent' : 'Sending a message will pause the bot'}
                          </p>
                        </div>
                      </div>

                      {/* ── RIGHT SIDEBAR — CONTACT INFO ── */}
                      <div className="hidden xl:flex w-[260px] bg-white border-l border-slate-200 flex-col shrink-0 overflow-y-auto custom-scrollbar">

                        {/* Contact header */}
                        <div className="p-5 border-b border-slate-100 flex flex-col items-center text-center gap-2">
                          {headerAvatarUrl ? (
                            <img src={headerAvatarUrl} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 shadow-sm" />
                          ) : (
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-sm ${headerAvatarColor}`}>
                              {headerAvatarChar}
                            </div>
                          )}
                          <div>
                            <h3 className="font-bold text-slate-800 text-[15px]">{headerRealName}</h3>
                            <p className="text-[12px] text-slate-500 mt-0.5">+{String(selectedChat.id).replace(/\D/g, '')}</p>
                          </div>
                          {/* Bot status pill */}
                          <span className={`text-[11px] font-semibold px-3 py-1 rounded-full ${isSelectedAiPaused ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                            {isSelectedAiPaused ? '🧑 Human Mode' : '🤖 Bot Active'}
                          </span>
                        </div>

                        {/* Details */}
                        <div className="p-4 space-y-4 text-[12px]">

                          {/* Lead status */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Lead Status</p>
                            <div className="flex flex-wrap gap-1.5">
                              {['HOT', 'WARM', 'COLD', 'WON', 'DEAD'].map(s => (
                                <button
                                  key={s}
                                  onClick={() => updateLeadStatus(selectedChat, s)}
                                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-all ${
                                    selectedChat.status === s
                                      ? s === 'HOT' ? 'bg-rose-500 text-white border-rose-500'
                                        : s === 'WON' ? 'bg-emerald-500 text-white border-emerald-500'
                                        : s === 'DEAD' ? 'bg-slate-500 text-white border-slate-500'
                                        : 'bg-indigo-500 text-white border-indigo-500'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                  }`}
                                >{s}</button>
                              ))}
                            </div>
                          </div>

                          {/* Info rows */}
                          {[
                            { label: 'Source', value: selectedChat.source || 'Organic', icon: '📍' },
                            { label: 'Course Interest', value: selectedChat.courseInterest || selectedChat.interestedCourse || selectedChat.query || '—', icon: '🎓' },
                            { label: 'First Seen', value: sidebarEntryTime.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }), icon: '📅' },
                            { label: 'Last Active', value: (getValidDateObj(selectedChat.updatedAt) || sidebarEntryTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }), icon: '🕐' },
                            { label: 'Business', value: tenants.find(t => t.whatsappId === selectedChat.clientId)?.name || 'Direct', icon: '🏢' },
                          ].map(row => (
                            <div key={row.label}>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{row.icon} {row.label}</p>
                              <p className="text-[12px] text-slate-700 font-medium bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">{row.value}</p>
                            </div>
                          ))}

                          {/* Notes */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">📝 Notes</p>
                            <textarea
                              placeholder="Add a note about this lead..."
                              defaultValue={selectedChat.notes || ''}
                              onBlur={async (e) => {
                                try {
                                  const { doc, updateDoc } = await import('firebase/firestore');
                                  if (selectedChat.clientId) {
                                    await updateDoc(doc(db, 'clients', selectedChat.clientId, 'chats', selectedChat.id), { notes: e.target.value });
                                  }
                                  await updateDoc(doc(db, 'users', selectedChat.id), { notes: e.target.value });
                                } catch(err) { console.error(err); }
                              }}
                              rows={3}
                              className="w-full text-[12px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-300 resize-none placeholder:text-slate-400"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })() : (
                    /* ── EMPTY STATE ── */
                    <div className="flex flex-col items-center justify-center h-full bg-[#f5f6fa] gap-4">
                      <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-200">
                        <MessageSquare size={36} className="text-slate-300" />
                      </div>
                      <div className="text-center">
                        <h3 className="text-[16px] font-bold text-slate-700">Select a conversation</h3>
                        <p className="text-[13px] text-slate-400 mt-1 max-w-[280px]">Click on any contact from the left to view their chat and manage their profile.</p>
                      </div>
                      <div className="flex items-center gap-6 mt-2">
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                          <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full" /> Bot Active
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                          <span className="w-2.5 h-2.5 bg-amber-400 rounded-full" /> Human Mode
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                          <span className="w-2.5 h-2.5 bg-rose-400 rounded-full animate-pulse" /> Unread
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })()}

          {/* ========================================= */}
          {/* MARKETING HUB VIEW & OTHERS... */}
          {/* ========================================= */}
          {activeView === 'marketing' && (
              <div className="space-y-6 animate-in fade-in duration-500 p-4 md:p-8">
                <div className="flex justify-between items-end mb-6 md:mb-8">
                  <div>
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight uppercase">Marketing Hub</h2>
                    <p className="text-xs md:text-sm font-medium text-slate-500 mt-1">Generate Review Links, QR Codes</p>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                  <div className="w-full lg:w-1/3 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden p-5 md:p-6 flex flex-col">
                     <div className="flex items-center gap-2 mb-3 md:mb-4">
                        <Store size={18} className="text-indigo-500" />
                        <h3 className="text-xs md:text-sm font-black text-slate-900 uppercase tracking-widest">Select Business</h3>
                     </div>
                     <select 
                        value={selectedQrClient} 
                        onChange={(e) => setSelectedQrClient(e.target.value)} 
                        className="w-full p-2.5 md:p-3 rounded-xl border border-slate-200 text-xs md:text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 cursor-pointer shadow-inner"
                     >
                        <option value="">-- Choose Tenant --</option>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                     </select>
                  </div>
                  <div className="w-full lg:w-2/3">
                    {!selectedQrClient ? (
                        <div className="h-full min-h-[200px] md:min-h-[300px] border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-slate-400 bg-slate-50 p-6 text-center">
                            <QrCode size={40} className="mb-3 opacity-50" />
                            <p className="font-bold text-sm md:text-base">Select a business to generate assets</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center w-full max-w-sm mx-auto lg:mx-0">
                            <h3 className="mt-4 md:mt-8 text-base md:text-lg font-black text-slate-900 mb-4 tracking-tight">Magic Review QR</h3>
                            <div className="bg-white p-3 md:p-4 rounded-2xl shadow-sm border border-slate-100 mb-4 md:mb-6">
                               <QRCodeSVG value={`https://pivot-rank.web.app//rate/${selectedQrClient}`} size={160} className="w-32 h-32 md:w-40 md:h-40" />
                            </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeView === 'billing' && (
              <div className="space-y-6 animate-in fade-in duration-500 p-4 md:p-8">
                <div className="mb-6 md:mb-8"><h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight uppercase">Financial Control</h2></div>
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden w-full overflow-x-auto">
                  <table className="w-full text-left min-w-[600px]">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400">
                      <tr><th className="px-4 md:px-6 py-3 md:py-4">Client Business</th><th className="px-4 md:px-6 py-3 md:py-4">Plan Rate</th><th className="px-4 md:px-6 py-3 md:py-4">Payment Status</th><th className="px-4 md:px-6 py-3 md:py-4 text-right">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredTenants.map((client) => (
                        <tr key={client.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 md:px-6 py-3 md:py-4"><p className="text-xs md:text-sm font-bold text-slate-900">{client.name}</p></td>
                          <td className="px-4 md:px-6 py-3 md:py-4"><p className="text-xs md:text-sm font-black text-slate-800">₹{client.planPrice || 0}</p></td>
                          <td className="px-4 md:px-6 py-3 md:py-4">{client.status === 'ACTIVE' ? (<span className="px-2 py-1 rounded-md text-[8px] md:text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">Paid</span>) : (<span className="px-2 py-1 rounded-md text-[8px] md:text-[9px] font-black uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-100">Pending</span>)}</td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-right flex justify-end"><button className="px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-lg text-[9px] md:text-[10px] font-black uppercase tracking-wider hover:bg-indigo-600 hover:text-white transition-all whitespace-nowrap">Send Link</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

        {activeView === 'pricing' && ( <div className="p-4 md:p-8"><PricingController /></div> )}
            {activeView === 'spyRadar' && ( <div className="p-4 md:p-8"><AdminSpyRadar /></div> )}
            {activeView === 'audience' && ( <div className="p-4 md:p-8 h-full"><AudienceManager /></div> )}
            
           {/* 👇 CLEAN CSS WRAPPER KE SATH NAYA CONTROLLER 👇 */}
            {activeView === 'optimizer' && ( 
                <div className="p-4 md:p-8 w-full animate-in fade-in duration-500">
                    <AdminSeoController tenants={tenants} />
                </div> 
            )}
            
            {activeView === 'social' && ( <div className="p-0 md:p-0 h-full overflow-y-auto"><SocialAutopilot tenants={tenants} /></div> )}
            {activeView === 'waSetup' && ( <div className="p-0 md:p-0 h-full overflow-y-auto"><AdminClientSetup /></div> )}
            {activeView === 'blog' && ( <div className="p-4 md:p-8 h-full overflow-y-auto"><SeoBlogEngine tenants={tenants} /></div> )}
          </main>
      </div>

      {/* DRAWERS & MODALS */}
      <BusinessProfileDrawer isOpen={isProfileDrawerOpen} onClose={() => setIsProfileDrawerOpen(false)} businessData={selectedBusiness} />

      {/* TOAST NOTIFICATION */}
      {toastMsg && (
          <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 bg-slate-900 text-white p-4 rounded-2xl shadow-2xl z-[500] animate-in slide-in-from-bottom-5 fade-in duration-300 flex items-start gap-4 border border-slate-700 min-w-[280px]">
              <div className="bg-emerald-500/20 p-2 rounded-xl text-emerald-400 shrink-0">
                  <Bell size={20} className="animate-pulse" />
              </div>
              <div className="flex-1">
                  <h4 className="text-[10px] md:text-xs font-black uppercase tracking-widest text-emerald-400">{toastMsg.title}</h4>
                  <p className="text-xs md:text-sm text-slate-200 font-medium mt-1">{toastMsg.desc}</p>
                  <p className="text-[9px] text-slate-500 mt-2 font-mono">{toastMsg.time}</p>
              </div>
              <button onClick={() => setToastMsg(null)} className="text-slate-500 hover:text-white transition-colors shrink-0">
                  <X size={16} />
              </button>
          </div>
      )}

      {/* CAMPAIGN BUILDER MODAL */}
      {isBlastModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200" style={{ zIndex: 99999 }}>
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-6xl h-[95vh] md:h-[85vh] flex flex-col overflow-hidden relative border border-slate-200/50">
            <div className="px-6 md:px-8 py-5 border-b border-slate-100 flex justify-between items-center bg-white z-10 shrink-0">
               <div className="flex items-center gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100"><Rocket size={24} className="animate-pulse" /></div>
                  <div>
                     <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">Campaign Builder</h2>
                     <p className="text-[10px] md:text-xs font-bold text-slate-500 mt-1 uppercase tracking-widest flex items-center gap-2"><Users size={12}/> Targeting {leadsList.length || 'Multiple'} Contacts</p>
                  </div>
               </div>
               <button onClick={() => setIsBlastModalOpen(false)} className="w-10 h-10 bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-full flex items-center justify-center transition-all"><X size={20}/></button>
            </div>
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden bg-slate-50/50">
               <div className="w-full lg:w-[55%] p-6 md:p-8 overflow-y-auto custom-scrollbar flex flex-col gap-6 md:gap-8 bg-white z-10">
                  <div className="space-y-3">
                     <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">1</span> Audience Selection</h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <select value={blastClientId} onChange={(e) => setBlastClientId(e.target.value)} className="w-full p-3.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50/50 bg-slate-50 transition-all cursor-pointer">
                           <option value="">Target Business...</option>
                           {tenants.filter(t => t.name && t.name.trim() !== "").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <select value={blastAudience} onChange={(e) => setBlastAudience(e.target.value)} className="w-full p-3.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50/50 bg-slate-50 transition-all cursor-pointer">
                           <option value="B2C_STUDENTS">👨‍🎓 B2C Students</option>
                           <option value="B2B_OWNERS">🏢 B2B Leads</option>
                        </select>
                     </div>
                  </div>
                  <div className="space-y-3">
                     <div className="flex justify-between items-center">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">2</span> Meta Templates</h3>
                        <button onClick={handleFetchTemplates} disabled={isFetchingTemplates} className="text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50">{isFetchingTemplates ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Sync Meta</button>
                     </div>
                     <div className="bg-white border border-slate-200 rounded-2xl p-1 shadow-sm transition-all focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-50/50">
                         {metaTemplates.length > 0 ? (
                            <select value={selectedTemplateIndex} onChange={handleTemplateSelect} className="w-full p-3 rounded-xl border-none bg-transparent text-sm font-bold text-slate-800 outline-none cursor-pointer focus:ring-0">
                                <option value="">Choose an approved template...</option>
                                {metaTemplates.map((t, idx) => (<option key={t.id} value={idx}>{t.name} ({t.language})</option>))}
                            </select>
                         ) : (<div className="p-4 text-center text-sm font-bold text-slate-400 bg-slate-50 rounded-xl">Click 'Sync Meta' to load templates</div>)}
                     </div>
                  </div>
                  <div className="space-y-3 flex-1 flex flex-col min-h-[250px]">
                     <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">3</span> Message Content</h3>
                     <div className="flex-1 flex flex-col border border-slate-200 rounded-2xl overflow-hidden focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-50/50 transition-all shadow-sm bg-white">
                        <div className="bg-slate-50/80 border-b border-slate-200 p-2.5 px-4 flex justify-between items-center">
                           <div className="flex gap-2"><button className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"><Italic size={16}/></button><button className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"><Strikethrough size={16}/></button></div>
                           <button onClick={() => setBlastMessage(prev => prev + " [Name]")} className="text-[11px] font-black text-indigo-600 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded-lg transition-colors tracking-widest uppercase">+ Add [Name]</button>
                        </div>
                        <textarea value={blastMessage} disabled={!blastClientId} onChange={(e) => setBlastMessage(e.target.value)} className="w-full flex-1 p-5 outline-none resize-none text-sm text-slate-700 disabled:bg-slate-50 leading-relaxed font-medium" placeholder="Type your WhatsApp broadcast message here..."></textarea>
                     </div>
                  </div>
               </div>
               <div className="hidden lg:flex w-[45%] bg-slate-100/50 border-l border-slate-200 p-4 md:p-8 items-center justify-center relative overflow-hidden">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-br from-indigo-100 to-emerald-50 opacity-60 blur-3xl rounded-full pointer-events-none"></div>
                  <div className="w-[280px] h-[85%] max-h-[550px] min-h-[400px] bg-slate-900 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.2)] p-2 relative z-10 shrink-0 ring-2 ring-slate-800 flex flex-col">
                     <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[80px] h-[22px] bg-black rounded-full z-30 shadow-inner"></div>
                     <div className="w-full h-full bg-[#efeae2] rounded-[32px] overflow-hidden flex flex-col relative border border-slate-800">
                        <div className="bg-[#008069] pt-10 pb-3 px-4 flex items-center gap-3 text-white shadow-md relative z-20 shrink-0">
                           <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center font-black text-sm backdrop-blur-sm">R</div>
                           <div><h4 className="text-[14px] font-bold leading-none">Rahul Sharma</h4><p className="text-[10px] text-white/80 font-medium mt-1">Live Preview</p></div>
                        </div>
                        <div className="flex-1 p-3 overflow-y-auto custom-scrollbar relative flex flex-col">
                           <div className="absolute inset-0 opacity-[0.06] pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
                           <div className="flex justify-center mb-4 mt-2 relative z-10"><span className="bg-[#E1F3FB] text-slate-600 text-[9px] font-bold px-2.5 py-1 rounded-md shadow-sm">TODAY</span></div>
                           <div className="bg-white p-2.5 rounded-2xl rounded-tl-sm shadow-sm max-w-[90%] text-[13px] text-slate-800 relative self-start group z-10">
                              <div className="whitespace-pre-wrap pr-10 leading-relaxed font-normal">{blastMessage ? blastMessage.replace(/\[Name\]/g, "Rahul Sharma").replace(/\{\{1\}\}/g, "Rahul Sharma") : <span className="text-slate-400 italic">Message preview...</span>}</div>
                              <span className="text-[9px] text-slate-400 absolute bottom-1 right-2 font-medium">12:00 PM</span>
                              <div className="absolute top-0 -left-1.5 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent"></div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <div className="px-6 md:px-8 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row justify-between items-center z-10 shrink-0 gap-4">
               <div className="flex items-center gap-4 w-full sm:w-auto">
                  <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 shrink-0"><Zap size={20}/></div>
                  <div><h4 className="text-sm font-black text-slate-900">Instant Dispatch</h4><p className="text-[11px] font-bold text-emerald-600 mt-0.5">Estimated Cost: ₹{(leadsList.length * 0.8).toFixed(2) || '1.60'}</p></div>
               </div>
               <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  <button onClick={() => setIsBlastModalOpen(false)} className="px-4 py-3 text-[11px] md:text-xs font-black text-slate-500 hover:text-slate-800 transition-colors uppercase tracking-widest">Cancel</button>
                  <button onClick={handleSendBlast} disabled={isBlasting || !blastMessage || !blastClientId} className="px-6 md:px-8 py-3.5 rounded-xl text-[11px] md:text-xs font-black text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2 shadow-lg shadow-indigo-200 transition-all disabled:opacity-50 disabled:shadow-none uppercase tracking-widest">
                     {isBlasting ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>} {isBlasting ? 'Dispatching...' : 'Launch Broadcast'}
                  </button>
               </div>
            </div>
          </div>
        </div>,
        document.body 
      )}

      {/* DEPLOY UNIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] p-2 md:p-4 animate-in zoom-in duration-200">
           <div className="bg-white rounded-2xl md:rounded-[32px] w-full max-w-3xl overflow-hidden shadow-2xl relative border border-slate-200 flex flex-col max-h-[95vh]">
              <div className="p-4 md:p-8 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-indigo-50/50 to-white shrink-0">
                 <div className="flex items-center gap-3 md:gap-4"><div className="bg-indigo-600 p-2 md:p-3 rounded-xl md:rounded-2xl shadow-lg shadow-indigo-200"><Rocket className="text-white animate-pulse w-5 h-5 md:w-6 md:h-6" /></div><div><h3 className="text-base md:text-xl font-black text-slate-900 tracking-tight">Deploy SaaS Instance</h3><p className="text-[9px] md:text-xs font-bold text-slate-400 uppercase tracking-widest mt-0.5">Provisioning System</p></div></div>
                 <button onClick={() => setIsModalOpen(false)} className="p-1.5 md:p-2 hover:bg-rose-50 hover:text-rose-600 text-slate-400 rounded-full transition-all"><X size={20}/></button>
              </div>
              {generatedPass ? (
                  <div className="p-8 md:p-12 text-center space-y-6 md:space-y-8 overflow-y-auto">
                     <div className="space-y-2"><div className="w-12 h-12 md:w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-6 h-6 md:w-8 h-8" /></div><h4 className="text-xl md:text-2xl font-black text-slate-900">Instance Deployed!</h4></div>
                     <div className="bg-slate-900 p-6 md:p-8 rounded-2xl border-4 border-indigo-500/20 font-mono text-xl md:text-3xl font-black text-indigo-400 tracking-[0.2em] shadow-inner select-all cursor-pointer">{generatedPass}</div>
                     <button onClick={() => setIsModalOpen(false)} className="w-full bg-slate-900 text-white font-black py-4 md:py-5 rounded-2xl text-xs md:text-sm tracking-widest uppercase hover:bg-black transition-all shadow-xl">Back to Center</button>
                  </div>
              ) : (
                  <form onSubmit={handleDeployUnit} className="flex flex-col md:flex-row overflow-y-auto custom-scrollbar">
                      <div className="w-full md:w-1/2 p-5 md:p-8 space-y-4 md:space-y-6 border-b md:border-b-0 md:border-r border-slate-100">
                         <div className="flex items-center gap-2 mb-1 md:mb-2"><Settings size={14} className="text-indigo-500" /><p className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">Config</p></div>
                        <div className="space-y-3 md:space-y-4">
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Official Name</label><input type="text" required value={clientForm.name} onChange={e => setClientForm({...clientForm, name: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-bold focus:ring-2 focus:ring-indigo-50 outline-none mt-1" /></div>
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">WhatsApp Engine ID</label><input type="text" required value={clientForm.whatsappId} onChange={e => setClientForm({...clientForm, whatsappId: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-mono focus:ring-2 outline-none mt-1" /></div>
                            
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Meta Token (WhatsApp)</label><input type="text" value={clientForm.metaToken} onChange={e => setClientForm({...clientForm, metaToken: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-mono outline-none mt-1" /></div>
                            
                            <div><label className="text-[9px] md:text-[10px] font-bold text-blue-500 ml-1 uppercase">FB Page ID</label><input type="text" value={clientForm.fbPageId} onChange={e => setClientForm({...clientForm, fbPageId: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-mono outline-none mt-1" placeholder="Facebook Page ID" /></div>
                            <div><label className="text-[9px] md:text-[10px] font-bold text-blue-500 ml-1 uppercase">FB Page Token (Publishing)</label><input type="text" value={clientForm.fbPageToken} onChange={e => setClientForm({...clientForm, fbPageToken: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-mono outline-none mt-1" placeholder="EAAG..." /></div>

                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Coordinator Phone</label><input type="text" value={clientForm.coordinatorPhone} onChange={e => setClientForm({...clientForm, coordinatorPhone: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-mono outline-none mt-1" /></div>
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Admin Email</label><input type="email" required value={clientForm.email} onChange={e => setClientForm({...clientForm, email: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-medium outline-none mt-1" /></div>
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Price (₹)</label><input type="number" value={clientForm.planPrice} onChange={e => setClientForm({...clientForm, planPrice: e.target.value})} className="w-full px-3 py-2 md:px-4 md:py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs md:text-sm font-black outline-none mt-1" /></div>
                         </div>
                      </div>
                      <div className="w-full md:w-1/2 p-5 md:p-8 space-y-4 md:space-y-6 bg-slate-50/50">
                         <div className="flex items-center gap-2 mb-1 md:mb-2"><BrainCircuit size={14} className="text-emerald-500" /><p className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">AI Intelligence</p></div>
                         <div className="space-y-3 md:space-y-4 flex flex-col h-full">
                            <div className="flex-1"><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Knowledge Base</label><textarea required value={clientForm.aiKnowledge} onChange={e => setClientForm({...clientForm, aiKnowledge: e.target.value})} className="w-full h-28 md:h-40 px-3 py-2 md:px-4 md:py-4 rounded-xl bg-white border border-slate-200 text-xs md:text-sm focus:ring-2 outline-none mt-1 resize-none shadow-sm" /></div>
                            <div><label className="text-[9px] md:text-[10px] font-bold text-slate-500 ml-1 uppercase">Bot Rules</label><textarea value={clientForm.aiRules} onChange={e => setClientForm({...clientForm, aiRules: e.target.value})} className="w-full h-16 md:h-24 px-3 py-2 md:px-4 md:py-4 rounded-xl bg-white border border-slate-200 text-xs md:text-sm focus:ring-2 outline-none mt-1 resize-none shadow-sm" /></div>
                            <div className="pt-2 md:pt-4">
                               <button type="submit" disabled={isCreating} className="w-full bg-indigo-600 text-white font-black py-3.5 md:py-5 rounded-xl md:rounded-2xl uppercase text-[10px] md:text-xs tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 flex items-center justify-center gap-2 disabled:opacity-50">
                                  {isCreating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Zap size={16}/> Launch Unit</>}
                               </button>
                            </div>
                         </div>
                      </div>
                  </form>
              )}
           </div>
        </div>
      )}

      {/* 👑 PREMIUM EXTEND MODAL (PREMIUM UI) */}
      {isExtendModalOpen && extendClientData && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[300] p-4 animate-in zoom-in duration-200">
           <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl relative border border-slate-200 flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-indigo-50/50 to-white">
                 <div className="flex items-center gap-4">
                     <div className="bg-indigo-600 p-3 rounded-2xl shadow-lg shadow-indigo-200">
                         <Crown className="text-white w-6 h-6" />
                     </div>
                     <div>
                         <h3 className="text-xl font-black text-slate-900 tracking-tight">Extend Premium</h3>
                         <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 truncate max-w-[200px]">{extendClientData.name}</p>
                     </div>
                 </div>
                 <button onClick={() => setIsExtendModalOpen(false)} className="p-2 hover:bg-rose-50 hover:text-rose-600 text-slate-400 rounded-full transition-all"><X size={20}/></button>
              </div>
              
              <div className="p-6 space-y-6">
                  {/* Status Cards */}
                  <div className="flex gap-4">
                      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Registered On</p>
                          <p className="text-sm font-semibold text-slate-800">
                              {getValidDateObj(extendClientData.createdAt)?.toLocaleDateString('en-IN') || 'N/A'}
                          </p>
                      </div>
                      <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-3">
                          <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Current Expiry</p>
                          <p className={`text-sm font-bold ${getValidDateObj(extendClientData.premiumExpiry) < new Date() ? 'text-rose-600' : 'text-amber-700'}`}>
                              {getValidDateObj(extendClientData.premiumExpiry)?.toLocaleDateString('en-IN') || 'Not Set / Expired'}
                          </p>
                      </div>
                  </div>

                 {/* Smart Quick Adjust Buttons */}
                  <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Quick Adjust</label>
                      <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => setExtendDays(30)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${extendDays === 30 ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-emerald-600 hover:bg-emerald-50'}`}>+ 30 Days</button>
                          <button onClick={() => setExtendDays(-30)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${extendDays === -30 ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-slate-200 text-rose-600 hover:bg-rose-50'}`}>- 30 Days</button>
                          
                          <button onClick={() => setExtendDays(90)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${extendDays === 90 ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-emerald-600 hover:bg-emerald-50'}`}>+ 90 Days</button>
                          <button onClick={() => setExtendDays(-90)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${extendDays === -90 ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-slate-200 text-rose-600 hover:bg-rose-50'}`}>- 90 Days</button>
                      </div>
                  </div>

                  {/* Custom Input */}
                  <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Custom Days (+ to Add, - to Reduce)</label>
                      <input 
                          type="number" 
                          value={extendDays} 
                          onChange={(e) => setExtendDays(Number(e.target.value))}
                          className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-800 font-bold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"
                      />
                  </div>

                  {/* Dynamic Calculation Preview */}
                  <div className={`border rounded-xl p-4 flex items-center justify-between transition-colors ${extendDays >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                      <div>
                          <p className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 ${extendDays >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>New Expiry Date Will Be</p>
                          <p className={`text-lg font-black ${extendDays >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                              {(() => {
                                  let d = getValidDateObj(extendClientData.premiumExpiry) || new Date();
                                  if (d < new Date() && extendDays > 0) d = new Date();
                                  d.setDate(d.getDate() + (Number(extendDays) || 0));
                                  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                              })()}
                          </p>
                      </div>
                      <Rocket className={`${extendDays >= 0 ? 'text-emerald-500' : 'text-rose-500'} opacity-50 w-8 h-8 transition-transform ${extendDays < 0 ? 'rotate-180' : ''}`} />
                  </div>
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50 flex gap-3">
                 <button onClick={() => setIsExtendModalOpen(false)} className="flex-1 py-3.5 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all text-sm">Cancel</button>
                 <button onClick={confirmExtendPremium} className="flex-[2] py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-xl shadow-lg shadow-indigo-200 transition-all text-sm flex items-center justify-center gap-2">
                    <CheckCircle2 size={18} /> Confirm Plan Extension
                 </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;